import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteExtDataCategory,
    getExtDataCategory,
    getExtDataCategories,
    setExtDataCategory,
    updateExtDataCategoriesOrder,
    setOpenModal,
    setFilters,
} from '../../store/extDataCategories/actions';
import constants from '../../commonUtils/constants';
import IconButton from '../../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const ExtDataCategoriesContext = createContext();

export const useExtDataCategories = () => useContext(ExtDataCategoriesContext);

export const ExtDataCategoriesProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { filters, ExtDataCategoriesList, loading, errors, openModal } = useSelector((state) => state.ExtDataCategories);
    const { extractionViewOnly, extractionData } = useSelector((state) => state.Extractions);
    const { extVersion } = useSelector((state) => state.ExtVersions);
    const versionId = extVersion?.id || extVersion?._id;

    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [localExtDataCategories, setLocalExtDataCategories] = useState([]);

    const debouncedGetExtDataCategories = useMemo(() => debounce((params) => {
        dispatch(getExtDataCategories(params));
    }, 300), [dispatch]);

    // Sync versionId and is_draft into Redux filters whenever scope changes
    useEffect(() => {
        if (!extractionData?.id) return;

        const updatedFilters = {
            ...filters,
            is_draft: extractionViewOnly && versionId ? false : true,
            versionId: extractionViewOnly && versionId ? versionId : null,
        };

        dispatch(setFilters(updatedFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractionViewOnly, versionId, extractionData?.id]);

    // Fetch whenever filters change (filters now includes versionId + is_draft)
    useEffect(() => {
        if (!extractionData?.id) return;
        debouncedGetExtDataCategories({ extId: extractionData.id, ...filters });
    }, [filters, extractionData?.id, debouncedGetExtDataCategories]);

    const extDataCategories = useMemo(() => {
        return ExtDataCategoriesList?.data?.map(
            (item, index) => ({
                id: `${item._id}_${index}`,
                _id: item._id,
                key: item.key,
                title: item.title,
                query: item.query,
                fields: item.fields,
                originalIndex: index + 1,
            })
        ) || [];
    }, [ExtDataCategoriesList]);

    useEffect(() => {
        setLocalExtDataCategories([]);
    }, [ExtDataCategoriesList?.data]);

    const displayData = localExtDataCategories.length > 0 ? localExtDataCategories : extDataCategories;

    const pagination = useMemo(() => ExtDataCategoriesList?.pagination, [ExtDataCategoriesList]);

    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetExtDataCategories({ extId: extractionData?.id, currentPage, perPage, ...filters });
    }, [filters, debouncedGetExtDataCategories, extractionData?.id]);

    const handleRowReorder = useCallback((reorderedData) => {
        const updatedData = reorderedData.map((item, index) => ({
            ...item,
            priority: index + 1,
        }));

        setLocalExtDataCategories(updatedData);

        const orderData = updatedData.map((item, index) => ({
            _id: item._id,
            priority: index + 1
        }));

        dispatch(updateExtDataCategoriesOrder(orderData));
    }, [dispatch]);

    const getRowId = useCallback((row) => {
        return row.id?.toString() || row._id?.toString() || '';
    }, []);

    const editExtDataCategory = useCallback((id) => {
        dispatch(setExtDataCategory(null));
        dispatch(setOpenModal(true));
        dispatch(getExtDataCategory(id));
    }, [dispatch]);

    const openAddModal = useCallback(() => {
        dispatch(setExtDataCategory(null));
        dispatch(setOpenModal(true));
    }, [dispatch]);

    const handleCloseModal = useCallback((action) => {
        if (action === 'delete') {
            setShowDeleteConfirmationDialog(false);
            setToDeleteId("");
        } else {
            dispatch(setOpenModal(false));
            dispatch(setExtDataCategory(null));
        }
    }, [dispatch]);

    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const handleDelete = useCallback(() => {
        dispatch(deleteExtDataCategory(toDeleteId));
        setToDeleteId("");
        setShowDeleteConfirmationDialog(false);
    }, [dispatch, toDeleteId]);

    const resetOrder = useCallback(() => {
        setLocalExtDataCategories([]);
    }, []);

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('originalIndex', { header: '#', cell: info => info.row.index + 1 }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    <IconButton
                        disabled={extractionViewOnly}
                        variant="action_default"
                        size="icon"
                        Icon={PencilSquareIcon}
                        onClick={() => editExtDataCategory(info.row.original._id)}
                    />
                    <IconButton
                        disabled={extractionViewOnly}
                        variant="action_default"
                        size="icon"
                        Icon={TrashIcon}
                        onClick={() => askDeleteConfirmation(info.row.original._id)}
                    />
                </>
            )
        }),
    ], [askDeleteConfirmation, columnHelper, editExtDataCategory, extractionViewOnly]);

    const table = useReactTable({
        data: displayData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowId: getRowId,
        initialState: {
            pagination: {
                pageIndex: (ExtDataCategoriesList?.pagination?.currentPage || 1) - 1,
                pageSize: ExtDataCategoriesList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            },
        },
        manualPagination: true,
        pageCount: ExtDataCategoriesList?.pagination?.totalPages,
    });

    return (
        <ExtDataCategoriesContext.Provider value={{
            extDataCategories: displayData,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editExtDataCategory,
            openAddModal,
            openModal,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
            handleRowReorder,
            getRowId,
            resetOrder,
        }}>
            {children}
        </ExtDataCategoriesContext.Provider>
    );
};